import React from "react";
import { TrendingDown } from "lucide-react";

/**
 * Quantity-tier pricing table — shown on ProductDetail when a SKU has
 * price_tiers configured. Highlights the current tier based on `qty` so
 * the customer instantly sees their savings as they bump the quantity.
 *
 * Compliance-neutral — purely a pricing display, no claims of any kind.
 */
export default function QuantityTierTable({ product, qty = 1 }) {
  const tiers = product?.price_tiers;
  if (!tiers || tiers.length === 0) return null;
  // Hide tier pricing whenever the SKU is on offer — bulk-discount ladder
  // contradicts the headline sale price and confuses customers.
  if (product?.on_sale) return null;

  // Build a complete ladder: base tier (1) + each configured tier
  const base = { min_qty: 1, unit_price: Number(product.price) };
  const ladder = [base, ...[...tiers].sort((a, b) => a.min_qty - b.min_qty)];

  const activeIdx = (() => {
    let best = 0;
    ladder.forEach((t, i) => { if (qty >= Number(t.min_qty)) best = i; });
    return best;
  })();

  return (
    <div className="mt-5 rounded-sm border border-[#1E2E44] bg-[#0B1220] overflow-hidden" data-testid="tier-pricing-table">
      <div className="px-4 py-2.5 border-b border-[#1E2E44] flex items-center gap-2 bg-[#0F172A]">
        <TrendingDown size={14} className="text-[#A6FF00]" />
        <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00]">
          Volume-research pricing
        </span>
      </div>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-[#9CA8B8]">
            <th className="text-left px-4 py-2 pg-mono uppercase tracking-[0.16em] text-[10px] font-normal">Vials</th>
            <th className="text-right px-4 py-2 pg-mono uppercase tracking-[0.16em] text-[10px] font-normal">Each</th>
            <th className="text-right px-4 py-2 pg-mono uppercase tracking-[0.16em] text-[10px] font-normal">You save</th>
          </tr>
        </thead>
        <tbody>
          {ladder.map((t, i) => {
            const isActive = i === activeIdx;
            const savings = Number(product.price) - Number(t.unit_price);
            const range = (() => {
              const next = ladder[i + 1];
              if (!next) return `${t.min_qty}+`;
              return Number(next.min_qty) - 1 === Number(t.min_qty)
                ? `${t.min_qty}`
                : `${t.min_qty}–${Number(next.min_qty) - 1}`;
            })();
            return (
              <tr
                key={i}
                className={`border-t border-[#1E2E44] transition-colors ${isActive ? "bg-[#A6FF00]/[0.06]" : ""}`}
                data-testid={`tier-row-${i}`}
              >
                <td className={`px-4 py-2.5 ${isActive ? "text-[#A6FF00] font-semibold" : "text-[#D5DCE8]"}`}>{range}</td>
                <td className={`px-4 py-2.5 text-right ${isActive ? "text-[#A6FF00] font-bold" : "text-[#F7FAFC]"}`}>
                  £{Number(t.unit_price).toFixed(2)}
                </td>
                <td className={`px-4 py-2.5 text-right ${isActive ? "text-[#5EEAD4]" : "text-[#9CA8B8]"}`}>
                  {savings > 0 ? `£${savings.toFixed(2)} ea` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[10.5px] text-[#9CA8B8] bg-[#0F172A] border-t border-[#1E2E44]">
        Bulk-research pricing applied automatically at checkout.
      </div>
    </div>
  );
}
